import { useQuery, type UseQueryOptions } from '@tanstack/vue-query'
import { defaultQueryOptions } from '@/lib/api/query-config'
import { computed } from 'vue'
import { retryUnlessClientError } from '@/lib/api/litta-api-error'
import {
    fetchMeCached,
    isRetryingRoles,
    ME_TTL_MS,
    type Me,
    RETRY_AFTER_FAILURE_MS,
    useCurrentUid,
} from './fetchers'

const ROLES_REFRESH_MS = 2 * 60_000

export const meQueryKeys = {
    forUser: (uid: string | null) => ['auth', 'me', uid] as const,
}

/**
 * The signed-in person's roles. Cached, because every gated area asks and the
 * answer only changes when somebody's access is changed. The key follows the
 * signed-in uid, so a sidebar that outlives a sign-out re-queries for whoever
 * signs in next rather than staying subscribed to the previous account.
 */
export function useMe(
    options?: Omit<UseQueryOptions<Me, Error>, 'queryKey' | 'queryFn'>,
) {
    const uid = useCurrentUid()
    return useQuery({
        ...defaultQueryOptions,
        queryKey: computed(() => meQueryKeys.forUser(uid.value)),
        queryFn: fetchMeCached,
        staleTime: ME_TTL_MS,
        refetchInterval: () =>
            isRetryingRoles() ? RETRY_AFTER_FAILURE_MS : ROLES_REFRESH_MS,
        refetchOnWindowFocus: true,
        retry: retryUnlessClientError,
        ...(options || {}),
    })
}
